import React, { useEffect, useState } from "react";
import { GetToken } from "../../components/Session";
import configData from "../../config.json";

const PERIOD_LABELS = {
    financial: { heading: "Financial Year", previous: "Previous financial year" },
    yearly: { heading: "Yearly", previous: "Previous year" },
    monthly: { heading: "Monthly", previous: "Previous month" },
    weekly: { heading: "Weekly", previous: "Previous week" },
    daily: { heading: "Daily", previous: "Yesterday" },
};

const METRICS = [
    { key: "weight", previous: "previousWeight", total: "previousFinalWeight", title: "Net Weight", icon: "fa-weight-hanging", accent: "blue", format: "weight" },
    { key: "transactions", previous: "previousTransactions", total: "previousFinalTransactions", title: "No. of Transactions", icon: "fa-file-lines", accent: "green", format: "count" },
    { key: "sales", previous: "previousSales", total: "previousFinalSales", title: "Sales", icon: "fa-dollar-sign", accent: "orange", format: "currency" },
    { key: "average", previous: "previousAverage", total: "previousFinalAverage", title: "Average $/t", icon: "fa-chart-column", accent: "purple", format: "currency" },
];

function metricNumber(value) {
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value !== "string" || !value.trim()) return null;
    // Summary responses include both numeric strings and compact currency values.
    const match = value.trim().match(/^([+-]?\$?(?:\d+(?:,\d{3})*(?:\.\d+)?|\.\d+))\s*([kmb])?$/i);
    if (!match) return null;
    const multiplier = { k: 1000, m: 1000000, b: 1000000000 }[match[2]?.toLowerCase()] || 1;
    const number = Number(match[1].replace(/[$,]/g, "")) * multiplier;
    return Number.isFinite(number) ? number : null;
}

export function formatMetric(value, format) {
    const number = metricNumber(value);
    if (number === null) return "—";
    if (format === "currency" && typeof value === "string" && value.includes("$")) return value.trim();
    if (format === "currency") return number.toLocaleString("en-AU", { style: "currency", currency: "AUD" });
    if (format === "weight") return number.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " t";
    return number.toLocaleString("en-AU", { maximumFractionDigits: 0 });
}

export function percentageChange(current, previous) {
    const now = metricNumber(current);
    const before = metricNumber(previous);
    if (now === null || before === null || before === 0) return null;
    return ((now - before) / Math.abs(before)) * 100;
}

const DashboardCards = ({ period }) => {
    const [summary, setSummary] = useState(null);
    const [error, setError] = useState("");
    const labels = PERIOD_LABELS[String(period).toLowerCase()] || PERIOD_LABELS.daily;

    useEffect(() => {
        const controller = new AbortController();
        setSummary(null);
        setError("");
        async function loadSummary() {
            try {
                const response = await fetch(configData.SERVER_URL + "apiV7/dashboard/getsummaryforperiod?period=" + encodeURIComponent(period), {
                    headers: { Authorization: "Bearer " + GetToken() },
                    signal: controller.signal,
                });
                if (!response.ok) throw new Error("Summary unavailable");
                setSummary(await response.json());
            } catch (error) {
                if (error.name !== "AbortError") setError("Summary figures could not be loaded. Please refresh to try again.");
            }
        }
        loadSummary();
        return () => controller.abort();
    }, [period]);

    return (
        <>
            {error && <p className="wm-dashboard__error" role="alert">{error}</p>}
            <div className="wm-summary" role="list" aria-label="Dashboard summary" aria-busy={!summary && !error}>
                {METRICS.map((metric) => {
                    const change = percentageChange(summary?.[metric.key], summary?.[metric.previous]);
                    const direction = change === null || change === 0 ? "neutral" : change > 0 ? "up" : "down";
                    return (
                        <article className={"wm-metric wm-metric--" + metric.accent} key={metric.key} role="listitem">
                            <div className="wm-metric__top">
                                <span className="wm-metric__icon"><i className={"fa-solid " + metric.icon} aria-hidden="true" /></span>
                                <div className="wm-metric__body">
                                    <h2>{metric.key === "weight" ? labels.heading + " " : ""}{metric.title}</h2>
                                    <div className="wm-metric__value">{formatMetric(summary?.[metric.key], metric.format)}</div>
                                    <div className="wm-metric__comparison">
                                        {change !== null && <span className={"wm-metric__change wm-metric__change--" + direction}>
                                            {change > 0 ? "▲ " : change < 0 ? "▼ " : ""}{Math.abs(change).toLocaleString("en-AU", { maximumFractionDigits: 1 })}%
                                        </span>}
                                        <span>vs {formatMetric(summary?.[metric.previous], metric.format)} at this point {labels.previous.toLowerCase()}</span>
                                    </div>
                                </div>
                                <i className="fa-solid fa-chart-simple wm-metric__spark" aria-hidden="true" />
                            </div>
                            <div className="wm-metric__footer">
                                <span>{labels.previous} {metric.key === "average" ? "average" : "total"}</span>
                                <strong>{formatMetric(summary?.[metric.total], metric.format)}</strong>
                            </div>
                        </article>
                    );
                })}
            </div>
        </>
    );
};

export default DashboardCards;

